import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { addNotification } from '../../store/slices/notificationsSlice';
import { useGetRestaurantCashStatusQuery } from '../../services/cashRegisterApi';
import { useHubEvent } from '../../hooks/useHubEvent';
import { TABLES_CHANGED_EVENT, CASH_MOVEMENTS_CHANGED_EVENT } from '../../services/realtimeHub';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { isAdmin, isManager, isCashier, isCashierWaiter } from '../../utils/roleHelpers';

const DISMISS_KEY = 'dismissedCashStatusAlert';

/**
 * Banner global: no hay NINGUNA caja abierta y vigente en el restaurante y ya
 * hay órdenes abiertas — avisa antes de que alguien llegue al cobro y le truene.
 * Solo lo ven los roles que cobran o supervisan (el backend impone lo mismo con
 * roles en el endpoint). Tiempo real como optimización; el polling lento es el
 * fallback (convención del hub compartido).
 */
const CashRegisterStatusAlert: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const { user } = useAppSelector((state) => state.auth);
  const [dismissedReason, setDismissedReason] = useState<string | null>(
    () => sessionStorage.getItem(DISMISS_KEY)
  );
  // Campanita: solo una notificación por transición ok→alerta
  const notifiedReasonRef = useRef<string | null>(null);

  const canSee =
    isAdmin(user?.role) || isManager(user?.role) || isCashier(user?.role) || isCashierWaiter(user?.role);

  const { data: status, refetch } = useGetRestaurantCashStatusQuery(undefined, {
    skip: !canSee,
    pollingInterval: 90000,
  });

  useHubEvent(TABLES_CHANGED_EVENT, () => refetch(), canSee);
  useHubEvent(CASH_MOVEMENTS_CHANGED_EVENT, () => refetch(), canSee);

  const alertActive = !!status && !status.anyCashRegisterOpenAndCurrent && status.openOrdersCount > 0;

  // Al volver a ok se limpia el dismiss para que una nueva degradación reaparezca
  useEffect(() => {
    if (status && !alertActive) {
      sessionStorage.removeItem(DISMISS_KEY);
      setDismissedReason(null);
      notifiedReasonRef.current = null;
    }
  }, [status, alertActive]);

  const openedAt = status?.oldestOpenRegisterOpenTime
    ? formatInTimeZone(status.oldestOpenRegisterOpenTime, 'DD/MM/YYYY HH:mm')
    : '';

  const messageText =
    status?.reason === 'stale'
      ? `La caja abierta corresponde a un día anterior (abierta el ${openedAt}). Ciérrala y abre una nueva para el turno actual; ya hay ${status.openOrdersCount} orden(es) abierta(s).`
      : `No hay ninguna caja abierta para el turno actual y ya hay ${status?.openOrdersCount ?? 0} orden(es) abierta(s). Abre una caja antes de cobrar.`;

  useEffect(() => {
    if (alertActive && status && notifiedReasonRef.current !== status.reason) {
      notifiedReasonRef.current = status.reason;
      dispatch(
        addNotification({
          type: 'warning',
          title: status.reason === 'stale' ? 'Caja de turno anterior' : 'Caja no abierta',
          message: messageText,
          actionUrl: '/cash-register',
        })
      );
    }
    // messageText deriva de status; basta con reaccionar a status/alertActive
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertActive, status, dispatch]);

  if (!canSee || !alertActive) return null;
  if (dismissedReason === status!.reason) return null;
  // Dentro de la pantalla de caja el banner es redundante
  if (location.pathname.startsWith('/cash-register')) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, status!.reason);
    setDismissedReason(status!.reason);
  };

  const title = status!.reason === 'stale' ? 'Caja de turno anterior' : 'Caja no abierta';
  const inlineText =
    status!.reason === 'stale'
      ? `abierta el ${openedAt}. Ciérrala y abre una del turno actual.`
      : `hay ${status!.openOrdersCount} orden(es) abierta(s). Ábrela antes de cobrar.`;

  return (
    <Alert
      type="warning"
      showIcon
      closable
      onClose={handleDismiss}
      style={{ marginBottom: 12, borderRadius: 8, padding: '8px 12px', alignItems: 'center' }}
      message={
        <>
          <strong>{title}</strong> — {inlineText}
        </>
      }
      action={
        <Button type="primary" size="small" onClick={() => navigate('/cash-register')}>
          Ir a Caja
        </Button>
      }
    />
  );
};

export default CashRegisterStatusAlert;
